import { Fragment } from 'react'

import { cn } from '@/lib/cn'

export interface BreadcrumbItem {
  href?: string
  label: string
  onNavigate?: () => void
  title?: string
}

export interface BreadcrumbsProps {
  ariaLabel: string
  className?: string
  items: BreadcrumbItem[]
  variant?: 'compact' | 'pageHeader'
}

export function Breadcrumbs({
  ariaLabel,
  className,
  items,
  variant = 'compact',
}: BreadcrumbsProps) {
  const pageHeader = variant === 'pageHeader'

  return (
    <nav aria-label={ariaLabel} className={cn('min-w-0', className)}>
      <ol
        className={cn(
          'flex min-w-0 items-center gap-2',
          pageHeader ? 'text-[18px] leading-6' : 'text-sm leading-5',
        )}
      >
        {items.map((item, index) => {
          const current = index === items.length - 1

          return (
            <Fragment key={item.href ?? index}>
              {index > 0 ? (
                <li
                  aria-hidden
                  className={cn(
                    'shrink-0 text-[var(--color-text-subtle)]',
                    pageHeader ? 'font-normal' : null,
                  )}
                >
                  /
                </li>
              ) : null}
              <li className={cn('min-w-0', current ? 'truncate' : 'shrink-0')}>
                {!current && item.href ? (
                  <a
                    className={cn(
                      'rounded-sm text-[var(--color-text-muted)] underline-offset-4 transition-colors hover:text-[var(--color-primary)] hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                      pageHeader ? 'font-medium' : null,
                    )}
                    href={item.href}
                    onClick={(event) => {
                      if (!item.onNavigate) return
                      event.preventDefault()
                      item.onNavigate()
                    }}
                    title={item.title}
                  >
                    {item.label}
                  </a>
                ) : pageHeader && current ? (
                  <h1
                    aria-current="page"
                    className="truncate text-[18px] leading-6 font-semibold text-[var(--color-text)]"
                    title={item.title ?? item.label}
                  >
                    {item.label}
                  </h1>
                ) : (
                  <span
                    aria-current={current ? 'page' : undefined}
                    className="font-medium text-[var(--color-text-strong)]"
                    title={item.title ?? item.label}
                  >
                    {item.label}
                  </span>
                )}
              </li>
            </Fragment>
          )
        })}
      </ol>
    </nav>
  )
}
